import {
  type BiomeId,
  brewableCount,
  type BrewRejected,
  type BrewResult,
  getItem,
  type InventoryState,
  type Recipe,
  recipesAt,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import {
  benchHead,
  firstShortfall,
  formatGold,
  goldNeed,
  itemRow,
  type Need,
  needIcon,
  needList,
  purseChip,
  setPurse,
  stepper,
  workAction,
} from './bench.js';
import { closeButton } from './hud.js';
import { describeOnHover, hideItemHover } from './item-hover.js';
import { kitTitle } from './kit.js';
import { uiFormat, uiText } from './text.js';
import './smith.css';
import './alchemy.css';

/**
 * The alchemist's window (ADR 0026): the two potions of the biome he stands
 * in, each as a card with what it takes from the bag and the purse for the
 * count chosen, set against what they hold, and the button that brews that
 * many. Like every panel it only sends the intent; what the bag holds
 * afterwards is the server's.
 */

export interface AlchemyPanelOptions {
  icons: CssAtlas | null;
  onBrew: (potion: string, count: number) => void;
}

const REFUSALS: Record<BrewRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'unknown-recipe': 'Cet alchimiste ne prépare pas cette potion.',
  'missing-materials': 'Il te manque des ingrédients.',
  'missing-gold': 'Ta bourse n’y suffit pas.',
  'bag-full': 'Ton sac est plein.',
  'too-far': 'Approche-toi de l’alchimiste.',
};

export class AlchemyPanel {
  private readonly root: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly list: HTMLElement;
  private readonly message: HTMLElement;
  private inventory: InventoryState | undefined;
  private biome: BiomeId | undefined;
  /** The count chosen per potion, kept while the window lives. */
  private readonly counts = new Map<string, number>();
  private inReach = false;
  private pending = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: AlchemyPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'smith alchemy';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Alchimiste'));

    const title = document.createElement('h2');
    title.className = 'smith__title';
    kitTitle(title, 'alchemy');
    title.textContent = uiText('Alchimiste');
    const close = closeButton('Fermer (Échap)', () => {
      this.close();
    });
    title.append(close);

    this.purse = purseChip();
    const head = benchHead(
      uiText('Rapporte-moi ce que laissent les bêtes du pays : j’en tire des potions de vie.'),
      this.purse,
    );
    this.list = document.createElement('div');
    this.list.className = 'bench__list alchemy__list';
    this.message = document.createElement('p');
    this.message.className = 'smith__message';
    this.message.setAttribute('aria-live', 'polite');

    this.root.append(title, head, this.list, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Opened at an alchemist: his biome decides the recipes. */
  open(biome: BiomeId): void {
    this.biome = biome;
    this.root.hidden = false;
    this.message.textContent = '';
    this.render();
  }

  close(): void {
    hideItemHover();
    this.root.hidden = true;
  }

  setInventory(state: InventoryState): void {
    this.inventory = state;
    this.pending = false;
    this.render();
  }

  setInReach(inReach: boolean): void {
    if (inReach === this.inReach) return;
    this.inReach = inReach;
    this.render();
  }

  showResult(result: BrewResult): void {
    this.pending = false;
    const potion = getItem(result.potion);
    this.message.textContent = uiFormat('{potion} × {count} pour {gold}.', {
      potion: uiText(potion?.name ?? result.potion),
      count: result.count,
      gold: formatGold(result.gold),
    });
    this.message.dataset.tone = 'success';
    this.root.classList.remove('smith--success');
    this.root.getBoundingClientRect();
    this.root.classList.add('smith--success');
    this.render();
  }

  showRejected(rejection: BrewRejected): void {
    this.pending = false;
    this.message.textContent = uiText(REFUSALS[rejection.reason]);
    this.message.dataset.tone = 'refused';
    this.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  /** A template across all the bag's stacks. */
  private have(templateId: string): number {
    return (this.inventory?.bag ?? [])
      .filter((entry) => entry.item.templateId === templateId)
      .reduce((sum, entry) => sum + entry.item.quantity, 0);
  }

  private render(): void {
    if (this.root.hidden || this.biome === undefined) return;
    setPurse(this.purse, this.inventory?.gold ?? 0);
    hideItemHover();
    const scroll = this.list.scrollTop;
    this.list.replaceChildren();
    if (!this.inReach) {
      const far = document.createElement('p');
      far.className = 'smith__warn alchemy__far';
      far.textContent = uiText('Approche-toi de l’alchimiste.');
      this.list.append(far);
    }
    for (const recipe of recipesAt(this.biome)) {
      const card = this.card(recipe);
      if (card !== undefined) this.list.append(card);
    }
    this.list.scrollTop = scroll;
  }

  private card(recipe: Recipe): HTMLElement | undefined {
    const potion = getItem(recipe.potion);
    if (potion === undefined) return undefined;
    const purse = this.inventory?.gold ?? 0;
    const most = brewableCount(recipe, (id) => this.have(id), purse);
    const count = Math.max(1, Math.min(this.counts.get(recipe.potion) ?? 1, most));

    const card = document.createElement('div');
    card.className = 'alchemy__recipe';
    card.dataset.template = recipe.potion;

    const { row, slot, text, meta } = itemRow(potion, this.options.icons);
    describeOnHover(slot, potion);
    const effects = [uiFormat('Rend {heal} points de vie', { heal: potion.heal ?? 0 })];
    if (potion.levelRequired > 1) {
      effects.push(uiFormat('niveau {level}', { level: potion.levelRequired }));
    }
    meta.textContent = effects.join(' · ');
    const ready = document.createElement('span');
    ready.className = most > 0 ? 'alchemy__ready' : 'alchemy__ready alchemy__ready--none';
    ready.textContent =
      most > 0
        ? uiFormat('Tu peux en préparer {count}', { count: most })
        : uiText('Il te manque de quoi en préparer une.');
    text.append(ready);
    card.append(row);

    const needs: Need[] = recipe.ingredients.map((ingredient) => {
      const template = getItem(ingredient.templateId);
      return {
        icon: needIcon(template, ingredient.templateId, this.options.icons),
        name: uiText(template?.name ?? ingredient.templateId),
        have: this.have(ingredient.templateId),
        need: ingredient.quantity * count,
      };
    });
    if (recipe.gold > 0) needs.push(goldNeed(recipe.gold * count, purse));
    card.append(needList(needs));

    const brew = document.createElement('div');
    brew.className = 'alchemy__brew';
    brew.append(
      stepper({
        count,
        most: Math.max(1, most),
        withMax: true,
        onStep: (next) => {
          this.counts.set(recipe.potion, next);
          this.render();
        },
      }),
    );
    // Out of reach is said once, over the list; the buttons only wait.
    const why = this.pending ? '' : (firstShortfall(needs) ?? (this.inReach ? undefined : ''));
    brew.append(
      workAction(
        uiFormat('Préparer ×{count} · {gold}', { count, gold: formatGold(recipe.gold * count) }),
        why,
        () => {
          this.pending = true;
          for (const other of this.list.querySelectorAll<HTMLButtonElement>('.bench__go')) {
            other.disabled = true;
          }
          this.options.onBrew(recipe.potion, count);
        },
      ),
    );
    card.append(brew);
    return card;
  }
}
